import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import cx from 'classnames';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { getThemeClassNames } from 'themes';
import { v4 as uuidv4 } from 'uuid';

import { AppDomain } from 'types/enums/AppDomain';
import { AuthUser } from 'types/interfaces/user/AuthUser';

import { PopularUser } from 'api/AuthApi';
import { getPhotoPlaceholders } from 'helpers/photoPlaceholders';
import { usePopularUserClickAnalytics } from 'hooks/analytics/usePopularUserClickAnalytics';
import { getUser } from 'store/auth/selectors';
import { getPopularUsers } from 'store/common/selectors';

import {
  ReactComponent as ArrowNext,
  ReactComponent as ArrowPrev,
} from 'assets/icons/swiper-arrow-prev.svg';

import { HotUserBadge } from 'components/shared/HotUserBadge';

import styles from './popularUsers.module.sass';

import 'swiper/swiper.min.css';

function createPopularUserFrom(user: AuthUser): PopularUser {
  return {
    id: user.id,
    photo_url: user.photo_url ?? getPhotoPlaceholders(user.gender, 1)[0],
  };
}

export const PopularUsers = () => {
  const popularUsers = useSelector(getPopularUsers);
  const user = useSelector(getUser);

  const { handlePopularUserClick } = usePopularUserClickAnalytics();

  const popularUsersNew = user?.is_vip
    ? [
        createPopularUserFrom(user),
        ...popularUsers.slice(0, popularUsers.length - 1),
      ]
    : popularUsers;

  const handleUserClick = (id: number) => () => {
    handlePopularUserClick(id);
  };

  const rootClassName = getThemeClassNames(styles.swiperArrows, {
    [AppDomain.MePlusYou]: styles.primary,
  });

  if (popularUsersNew.length) {
    return (
      <section className={styles.wrapper}>
        <Swiper
          loop={Boolean(popularUsers.length)}
          className={styles.swiper}
          freeMode={false}
          modules={[Navigation]}
          navigation={{
            nextEl: `.swiper-button-next`,
            prevEl: `.swiper-button-prev`,
          }}
          breakpoints={{
            768: {
              slidesPerView: 7,
              spaceBetween: 10,
            },
            850: {
              slidesPerView: 8,
            },
            92: {
              slidesPerView: 9,
            },
            998: {
              slidesPerView: 10,
            },
            1100: {
              slidesPerView: 11,
            },
            1250: {
              slidesPerView: 10,
              spaceBetween: 17,
            },
          }}
        >
          {popularUsersNew.map(({ photo_url, id }) => (
            <SwiperSlide key={photo_url + uuidv4()}>
              <Link
                to={`/user${id}`}
                role="listitem"
                aria-label="user profile"
                className={styles.photo}
                style={{
                  backgroundImage: `url(${photo_url})`,
                }}
                onClick={handleUserClick(id)}
              >
                <div className={styles.badge}>
                  <HotUserBadge />
                </div>
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>
        <ArrowPrev
          className={cx('swiper-button-prev', rootClassName, {
            [styles.arrowPrev]: true,
          })}
        />
        <ArrowNext
          className={cx('swiper-button-next', rootClassName, {
            [styles.arrowNext]: true,
          })}
        />
      </section>
    );
  }

  return null;
};
